const DICTIONARY = 'ABCDEFGHJKLMNOPQRSTUVWXYZabcdefhijkmnopqrstuvwxyz23456789'
const DICTIONARY_LENGTH = BigInt(DICTIONARY.length)
const SHARECODE_PATTERN = /^CSGO(-?[\w]{5}){5}$/

export class InvalidShareCode extends Error {
    constructor() {
        super('Invalid share code')
        Object.setPrototypeOf(this, InvalidShareCode.prototype)
    }
}

export class InvalidCrosshairShareCode extends Error {
    constructor() {
        super('Invalid crosshair share code')
        Object.setPrototypeOf(this, InvalidCrosshairShareCode.prototype)
    }
}

function bytesToHex(bytes) {
    return Array.from(bytes, (byte) => {
        return ('0' + (byte & 0xff).toString(16)).slice(-2)
    }).join('')
}

function bytesToBigInt(bytes) {
    const hex = bytesToHex(bytes)
    return BigInt(`0x${hex}`)
}

function stringToBytes(str) {
    const bytes = []
    for (let i = 0; i < str.length; i += 2) {
        bytes.push(parseInt(str.slice(i, i + 2), 16))
    }
    return bytes
}

function int16ToBytes(number) {
    return [(number & 0x0000ff00) >> 8, number & 0x000000ff]
}

function uint8ToInt8(number) {
    return (number << 24) >> 24
}

function sumArray(array) {
    return array.reduce((previousValue, value) => {
        return previousValue + value
    }, 0)
}

function shareCodeToBytes(shareCode) {
    if (!shareCode.match(SHARECODE_PATTERN)) {
        throw new InvalidShareCode()
    }

    shareCode = shareCode.replace(/CSGO|-/g, '')
    const chars = Array.from(shareCode).reverse()
    let big = BigInt(0)
    for (let i = 0; i < chars.length; i++) {
        big = big * DICTIONARY_LENGTH + BigInt(DICTIONARY.indexOf(chars[i]))
    }

    const str = big.toString(16).padStart(36, '0')
    const bytes = stringToBytes(str)

    return bytes
}

function bytesToShareCode(bytes) {
    const hex = bytesToHex(bytes)
    let total = BigInt(`0x${hex}`)
    let chars = ''
    let rem = BigInt(0)
    for (let i = 0; i < 25; i++) {
        rem = total % DICTIONARY_LENGTH
        chars += DICTIONARY[Number(rem)]
        total = total / DICTIONARY_LENGTH
    }

    return `CSGO-${chars.slice(0, 5)}-${chars.slice(5, 10)}-${chars.slice(10, 15)}-${chars.slice(15, 20)}-${chars.slice(
        20,
        25
    )}`
}

export function decodeCrosshairShareCode(shareCode) {
    const bytes = shareCodeToBytes(shareCode)
    const size = sumArray(bytes.slice(1)) % 256

    if (bytes[0] !== size) {
        throw new InvalidCrosshairShareCode()
    }

    const crosshair = {
        gap: uint8ToInt8(bytes[2]) / 10,
        outline: bytes[3] / 2,
        red: bytes[4],
        green: bytes[5],
        blue: bytes[6],
        alpha: bytes[7],
        splitDistance: bytes[8] & 7,
        followRecoil: ((bytes[8] >> 4) & 8) === 8,
        fixedCrosshairGap: uint8ToInt8(bytes[9]) / 10,
        color: bytes[10] & 7,
        outlineEnabled: (bytes[10] & 8) === 8,
        innerSplitAlpha: (bytes[10] >> 4) / 10,
        outerSplitAlpha: (bytes[11] & 0xf) / 10,
        splitSizeRatio: (bytes[11] >> 4) / 10,
        thickness: bytes[12] / 10,
        centerDotEnabled: ((bytes[13] >> 4) & 1) === 1,
        deployedWeaponGapEnabled: ((bytes[13] >> 4) & 2) === 2,
        alphaEnabled: ((bytes[13] >> 4) & 4) === 4,
        tStyleEnabled: ((bytes[13] >> 4) & 8) === 8,
        style: (bytes[13] & 0xf) >> 1,
        length: bytes[14] / 10,
    }

    return crosshair
}

export function crosshairToConVars(crosshair) {
    const commands = [
        `cl_crosshair_drawoutline ${Number(crosshair.outlineEnabled)}`,
        `cl_crosshair_dynamic_maxdist_splitratio ${crosshair.splitSizeRatio}`,
        `cl_crosshair_dynamic_splitalpha_innermod ${crosshair.innerSplitAlpha}`,
        `cl_crosshair_dynamic_splitalpha_outermod ${crosshair.outerSplitAlpha}`,
        `cl_crosshair_dynamic_splitdist ${crosshair.splitDistance}`,
        `cl_crosshair_outlinethickness ${crosshair.outline}`,
        `cl_crosshair_t ${Number(crosshair.tStyleEnabled)}`,
        `cl_crosshairalpha ${crosshair.alpha}`,
        `cl_crosshaircolor ${crosshair.color}`,
        `cl_crosshairdot ${Number(crosshair.centerDotEnabled)}`,
        `cl_crosshairgap ${crosshair.gap}`,
        `cl_crosshairgap_useweaponvalue ${Number(crosshair.deployedWeaponGapEnabled)}`,
        `cl_crosshairsize ${crosshair.length}`,
        `cl_crosshairstyle ${crosshair.style}`,
        `cl_crosshairthickness ${crosshair.thickness}`,
        `cl_crosshairusealpha ${Number(crosshair.alphaEnabled)}`,
        `cl_fixedcrosshairgap ${crosshair.fixedCrosshairGap}`,
        `cl_crosshair_recoil ${Number(crosshair.followRecoil)}`,
    ]

    const presetRgb = { 0: [255,0,0], 1: [0,255,0], 2: [255,255,0], 3: [0,0,255], 4: [0,255,255] }
    const rgb = crosshair.color === 5
        ? [crosshair.red, crosshair.green, crosshair.blue]
        : (presetRgb[crosshair.color] || [0,255,0])
    commands.push(`cl_crosshaircolor_r ${rgb[0]}`)
    commands.push(`cl_crosshaircolor_g ${rgb[1]}`)
    commands.push(`cl_crosshaircolor_b ${rgb[2]}`)

    return commands.join('; ')
}

export const normalizeValue = (val, step = 0.5) => {
    if (isNaN(val)) return 0;
    return Math.round(val / step) * step;
}

// Helper functions for drawing logic
export const calculatePixelSize = (size) => {
    let value = size
    let extraSize = 0
    if (value > 2 && value < 6) extraSize += 1
    else if (value >= 6 && value <= 12) extraSize += 2
    else if (value > 12 && value < 14) extraSize += 3
    else if (value >= 14 && value <= 18) extraSize += 4
    else if (value > 18 && value < 22) extraSize += 5
    else if (value >= 22) extraSize += 6
    return { value: value * 2 + extraSize, extra: extraSize }
}

export const calculateTickness = (tickness) => {
    let value = tickness
    let extra = 0
    if (value > 2 && value < 6) extra += 1
    else if (value >= 6 && value <= 12) extra += 2
    else if (value > 12 && value < 14) extra += 3
    else if (value >= 14 && value <= 18) extra += 4
    else if (value > 18 && value < 22) extra += 5
    else if (value >= 22) extra += 6
    return { value: value * 2 + extra, extra: extra }
}

export const calculateGap = (gap, thickness) => {
    return gap + calculateTickness(thickness).value / 2 + 4
}

export const calculateOutlineThickness = (active, tickness) => {
    if (!active) return 0
    if (tickness == 0.5) return 0.5
    return Math.ceil(tickness)
}

export const drawCrosshairOnContext = (ctx, w, h, params) => {
    const {
        size,
        thickness,
        gap,
        colorR,
        colorG,
        colorB,
        alpha,
        dot,
        outline,
        outlineThickness,
        isTStyle,

        // Optional parameters not always used in basic preview
        recoil = false,
        style = 4
    } = params

    const centerX = w / 2
    const centerY = h / 2

    // Note: We don't clear here to allow layering if needed, 
    // the caller (MiniPreview or MainCanvas) should handle clearRect.

    const pixelSize = calculatePixelSize(size).value
    const pixelThickness = calculateTickness(thickness).value
    const pixelOutlineThickness = calculateOutlineThickness(
        outline,
        outlineThickness
    )

    const pixelGap = calculateGap(gap, thickness)

    // Ensure colors are valid numbers
    const r = isNaN(colorR) ? 0 : colorR
    const g = isNaN(colorG) ? 255 : colorG
    const b = isNaN(colorB) ? 0 : colorB
    const a = isNaN(alpha) ? 255 : alpha

    const color = `rgba(${r}, ${g}, ${b}, ${a / 255})`

    const drawLine = (pos, c, oT) => {
        const totalThick = pixelThickness + 2 * oT
        const totalSz = pixelSize + 2 * oT
        const halfThick = pixelThickness / 2
        ctx.fillStyle = c
        let x, y, width, height
        if (pos === 'left') {
            x = centerX - pixelGap - pixelSize - oT
            y = centerY - halfThick - oT
            width = totalSz
            height = totalThick
        } else if (pos === 'right') {
            x = centerX + pixelGap - oT
            y = centerY - halfThick - oT
            width = totalSz
            height = totalThick
        } else if (pos === 'top') {
            x = centerX - halfThick - oT
            y = centerY - pixelGap - pixelSize - oT
            width = totalThick
            height = totalSz
        } else if (pos === 'bottom') {
            x = centerX - halfThick - oT
            y = centerY + pixelGap - oT
            width = totalThick
            height = totalSz
        }
        ctx.fillRect(
            Math.round(x),
            Math.round(y),
            Math.round(width),
            Math.round(height)
        )
    }

    const dirs = ['left', 'right', 'top', 'bottom']
    const activeDirs = isTStyle ? ['left', 'right', 'bottom'] : dirs

    if (outline && pixelOutlineThickness > 0) {
        const outlineColor = `rgba(0, 0, 0, ${a / 255})`
        activeDirs.forEach((d) =>
            drawLine(d, outlineColor, pixelOutlineThickness)
        )
    }
    activeDirs.forEach((d) => drawLine(d, color, 0))

    if (dot) {
        const dotOutSz =
            calculateTickness(thickness).value + pixelOutlineThickness * 2
        const halfOut = dotOutSz / 2
        if (outline && pixelOutlineThickness > 0) {
            ctx.fillStyle = `rgba(0, 0, 0, ${a / 255})`
            ctx.fillRect(
                Math.round(centerX - halfOut),
                Math.round(centerY - halfOut),
                Math.round(dotOutSz),
                Math.round(dotOutSz)
            )
        }
        const dotSz = calculateTickness(thickness).value
        const half = dotSz / 2
        ctx.fillStyle = color
        ctx.fillRect(
            Math.round(centerX - half),
            Math.round(centerY - half),
            Math.round(dotSz),
            Math.round(dotSz)
        )
    }
}

export const COLOR_MAP = {
    0: { r: 255, g: 0, b: 0 },
    1: { r: 0, g: 255, b: 0 },
    2: { r: 255, g: 255, b: 0 },
    3: { r: 0, g: 0, b: 255 },
    4: { r: 0, g: 255, b: 255 },
    5: { r: 255, g: 255, b: 255 },
}

export const parseCrosshairCommands = (str) => {
    if (!str) return {
        size: 2,
        thickness: 0.5,
        gap: -1,
        alpha: 255,
        colorR: 0,
        colorG: 255,
        colorB: 0,
        dot: false,
        outline: false,
        outlineThickness: 0.5,
        recoil: false,
        isTStyle: false,
        style: 4,
        friendlyWarning: 0,
        sniperWidth: 1
    }

    const commands = str
        .split(/[;\n]+/)
        .map((c) => c.trim())
        .filter((c) => c)

    // Default params
    const params = {
        size: 2,
        thickness: 0.5,
        gap: -1,
        alpha: 255,
        colorR: 0,
        colorG: 255,
        colorB: 0,
        dot: false,
        outline: false,
        outlineThickness: 0.5,
        recoil: false,
        isTStyle: false,
        style: 4,
        friendlyWarning: 0,
        sniperWidth: 1
    }

    commands.forEach((cmd) => {
        const cleanCmd = cmd.replace(/"/g, '')
        const [key, ...rest] = cleanCmd.split(' ')
        const val = rest.join(' ')

        if (key === 'cl_crosshairsize') params.size = normalizeValue(Math.max(0, parseFloat(val)), 0.5)
        if (key === 'cl_crosshairthickness') {
            const t = parseFloat(val)
            params.thickness = normalizeValue(t <= 0 ? 0.5 : t, 0.5)
        }
        if (key === 'cl_crosshairgap') params.gap = Math.round(parseFloat(val))
        if (key === 'cl_crosshairalpha') params.alpha = Math.max(0, Math.min(255, parseInt(val)))
        if (key === 'cl_crosshaircolor_r') params.colorR = Math.max(0, Math.min(255, parseInt(val)))
        if (key === 'cl_crosshaircolor_g') params.colorG = Math.max(0, Math.min(255, parseInt(val)))
        if (key === 'cl_crosshaircolor_b') params.colorB = Math.max(0, Math.min(255, parseInt(val)))
        if (key === 'cl_crosshaircolor') {
            const c = COLOR_MAP[parseInt(val)]
            if (c) {
                params.colorR = c.r
                params.colorG = c.g
                params.colorB = c.b
            }
        }
        if (key === 'cl_crosshairdot') params.dot = (val === '1' || val === 'true')
        if (key === 'cl_crosshair_drawoutline') params.outline = (val === '1' || val === 'true')
        if (key === 'cl_crosshair_outlinethickness') params.outlineThickness = normalizeValue(Math.max(0, parseFloat(val)), 0.5)
        if (key === 'cl_crosshair_recoil') params.recoil = (val === '1' || val === 'true')
        if (key === 'cl_crosshair_t') params.isTStyle = (val === '1' || val === 'true')
        if (key === 'cl_crosshairstyle') params.style = parseInt(val)
        if (key === 'cl_crosshair_friendly_warning') params.friendlyWarning = parseInt(val)
        if (key === 'cl_crosshair_sniper_width') params.sniperWidth = parseInt(val)
    })

    return params
}
